import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { UserPreferences } from "common";
import { ROUTES } from "@via/utils/constants";
import { useNavigate } from "react-router-dom";

const fetchUserPreferences = async (): Promise<UserPreferences> => {
  try {
    const res = await axios.get("/api/user/v1/preferences");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useUserPreferences = (): UseQueryResult<
  UserPreferences,
  Error
> => {
  return useQuery<UserPreferences, Error>({
    queryKey: ["/api/user/v1/preferences"],
    queryFn: async () => await fetchUserPreferences(),
    retry: false,
  });
};

export const useUpdateUserPreferences = () => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UserPreferences) => {
      const res = await axios.patch("/api/user/v1/preferences", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "user-preferences-updated-successfully",
        message: "User Preferences Updated Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/preferences"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-preferences-update-failed",
        message: "Failed to Update User Preferences",
        details:
          getErrorResponseMessage(error) ?? "Failed to update user preferences",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeactivateUser = (): UseMutationResult<
  void,
  ErrorNotification,
  void
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ErrorNotification>({
    mutationFn: async () => {
      await axios.post("/api/v1/user/deactivate");
    },
    onSuccess: (): void => {
      setNotification({
        id: "deactivate-account-success",
        message: "Account Deactivated Successfully",
        details:
          "Your account has been deactivated successfully. You will no longer have access to the platform.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      navigate(ROUTES.LOGIN);
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "deactivate-account-failed",
        message: "Deactivate Account Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
